import React, {useState, useEffect, useMemo, MouseEvent, FC} from 'react';
import moment from 'moment';
import MomentUtils from '@date-io/moment';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';

import {getLocale} from 'epic-common/src/locale';
import useLocalization from '../../../../../hooks/useLocalization';
import DatePickerOption from '../../../../common/date-range-picker/DatePickerOption';
import {DayRangePicker, YearMonthPicker, useRangePickerData} from '../../../../../components';
import {
    getDateline,
    GlobalDatePickerProps,
    DatePickerContainer,
    isSameDateRange,
    CalendarHeaderButton,
    isFuture,
    getYearMonthDate
} from '../../../../global-date-picker';
import PopperInput from '../../../../common/PopperInput';
import useTimeRangeWarning from '../components/time-range-limit/useTimeRangeWarning';
import {StyledPopover} from '../../../../widget-date-picker/WidgetDatePicker';
import {GAME_XP_PRESELECTED_DATE_RANGE, DateRangePickerOption} from '../constants';
import {getIsLast24Hours} from '../utils';

import {TimeScale, DateMoment} from '../../../../../types';

const CODES = {
    [DateRangePickerOption.PRESELECTED]: 'epic.affiliate.fortnite_games.xp.date_range.preselected',
    [DateRangePickerOption.CUSTOM]: 'epic.affiliate.fortnite_games.xp.date_range.custom'
};

export interface GameXpDatePickerProps extends Omit<GlobalDatePickerProps, 'onChange' | 'value'> {
    timeScale: TimeScale;
}

const GameXpDatePicker: FC<GameXpDatePickerProps> = ({
    timeScale,
    selectedDate: selected,
    startDate: start,
    endDate: end,
    onRangeChange
}: GameXpDatePickerProps) => {
    const {
        isPreselection,
        selectedDate,
        startDate,
        endDate,
        setIsPreselection,
        handleDayChange,
        handleYearMonthChange,
        setCalendar
    } = useRangePickerData({
        selectedDate: selected,
        startDate: start,
        endDate: end
    });
    const locale = getLocale();
    const getMessage = useLocalization();
    const [checkTimeRange] = useTimeRangeWarning();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [datePickerOption, setDatePickerOption] = useState(DateRangePickerOption.PRESELECTED);
    const isNewDateRange = !isSameDateRange({startDate: start, endDate: end}, {startDate, endDate});
    const [isYearMonthCalendar, setIsYearMonthCalendar] = useState(false);

    const open = Boolean(anchorEl);
    const id = open ? 'date-picker-popper' : undefined;

    const getIsDisabled = (value: DateRangePickerOption) =>
        value === DateRangePickerOption.CUSTOM &&
        getIsLast24Hours({startDate, endDate, selectedDate});

    const datePickerOptions = useMemo(() => {
        return Object.values(DateRangePickerOption).map(value => ({
            label: getMessage(CODES[value]),
            value
        }));
    }, []);

    const dateline = (selectedDate: DateMoment) =>
        getIsLast24Hours({startDate, endDate, selectedDate})
            ? datePickerOptions[0].label
            : getDateline({startDate, endDate});

    const handleOptionChange = (value: DateRangePickerOption) => {
        if (value === DateRangePickerOption.CUSTOM) {
            return;
        }

        setDatePickerOption(value);
        setCalendar(GAME_XP_PRESELECTED_DATE_RANGE);
    };

    const handleYearMonthCalendarSwitch = () => {
        setIsYearMonthCalendar(!isYearMonthCalendar);
    };

    const handleYearMonthRangeChange = (date: moment.Moment) => {
        const selectedDate = isFuture(date) ? getYearMonthDate(timeScale) : date;

        handleYearMonthChange(selectedDate);
    };

    const togglePopoverState = (event: MouseEvent<HTMLElement>) => {
        if (anchorEl) {
            if (isNewDateRange) {
                checkTimeRange(
                    {startDate, endDate, timeScale},
                    {
                        onConfirm: () => setAnchorEl(null)
                    }
                );
            } else {
                setAnchorEl(null);
            }
            return;
        }

        setAnchorEl(event.currentTarget);
    };

    useEffect(() => {
        setCalendar({startDate: start, endDate: end, selectedDate: selected});
    }, [start, end, selected]);

    useEffect(() => {
        if (getIsLast24Hours({startDate, endDate, selectedDate})) {
            setDatePickerOption(DateRangePickerOption.PRESELECTED);
        } else {
            setDatePickerOption(DateRangePickerOption.CUSTOM);
        }
    }, [startDate, endDate, selectedDate]);

    useEffect(() => {
        if (!open) {
            setIsPreselection(true);
            setIsYearMonthCalendar(false);
            onRangeChange({startDate, endDate, selectedDate});
        }
    }, [open]);

    return (
        <>
            <PopperInput open={open} value={dateline(selectedDate)} onClick={togglePopoverState} />
            <StyledPopover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={togglePopoverState}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}>
                <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale={locale}>
                    <DatePickerContainer>
                        <Box my={4}>
                            {datePickerOptions.map((option, i) => (
                                <React.Fragment key={option.value}>
                                    <DatePickerOption
                                        selected={datePickerOption === option.value}
                                        disabled={getIsDisabled(option.value)}
                                        onChange={handleOptionChange}
                                        {...option}
                                    />
                                    {i === 0 && (
                                        <Box mx={4} my={1}>
                                            <Divider />
                                        </Box>
                                    )}
                                </React.Fragment>
                            ))}
                        </Box>
                        {isYearMonthCalendar ? (
                            <YearMonthPicker
                                startDate={startDate}
                                endDate={endDate}
                                value={selectedDate}
                                onChange={handleYearMonthRangeChange}
                                onMonthChange={handleYearMonthCalendarSwitch}
                                onCalendarSwitch={handleYearMonthCalendarSwitch}
                            />
                        ) : (
                            <CalendarHeaderButton onClick={handleYearMonthCalendarSwitch}>
                                <DayRangePicker
                                    autoOk
                                    disableToolbar
                                    selectedDate={selectedDate}
                                    startDate={startDate}
                                    endDate={endDate}
                                    variant="static"
                                    openTo="date"
                                    value={selectedDate}
                                    onChange={handleDayChange}
                                    isPreselection={isPreselection}
                                />
                            </CalendarHeaderButton>
                        )}
                    </DatePickerContainer>
                </MuiPickersUtilsProvider>
            </StyledPopover>
        </>
    );
};

export default GameXpDatePicker;
